// Frais et débours d'un dossier (onglet Facturation du dossier) : liste, saisie, mandat de débours.
import { useState } from 'react';
import { toast } from 'sonner';
import { cn } from '../../lib/cn';
import { formatAppDate } from '../../lib/dateFormat';
import { formatEuros } from '../../services/billing/billingService';
import { expenseReceiptUrl, type DossierExpense } from '../../services/billing/expenseService';
import { useDeleteExpense, useExpenses, useSetDeboursMandate } from '../../hooks/queries/useExpenses';
import { useDossier } from '../../hooks/queries/useDossiers';
import { useCanAccessFeature } from '../../hooks/usePermissions';
import { EXPENSE_NATURE_LABELS, mandatePrefill, type ExpenseNature } from '../../lib/billing/expenses';
import { FaPen, FaTrashCan } from '../ui/FaIcons';
import { ExpenseFormDialog } from './ExpenseFormDialog';

export const NATURE_PILL: Record<ExpenseNature, string> = {
  debours: 'bg-[#eef1ea] text-[#4f5d45]',
  frais: 'bg-cime-gold-bg text-cime-gold-text',
};

export function ExpenseReceiptLink({ expense }: { expense: Pick<DossierExpense, 'receipt_path' | 'receipt_name'> }) {
  if (!expense.receipt_path) {
    return <span className="text-xs font-semibold text-cime-urgent">Justificatif manquant</span>;
  }
  return (
    <button
      type="button"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          window.open(await expenseReceiptUrl(expense.receipt_path!), '_blank', 'noopener');
        } catch (err) {
          toast.error(err instanceof Error ? err.message : 'Justificatif introuvable');
        }
      }}
      className="max-w-[160px] truncate text-xs text-cime-primary underline underline-offset-2"
      title={expense.receipt_name ?? 'Justificatif'}
    >
      {expense.receipt_name ?? 'Justificatif'}
    </button>
  );
}

function MandateLine({ dossierId, signedAt, canEdit }: { dossierId: string; signedAt: string | null; canEdit: boolean }) {
  const { data: dossier } = useDossier(dossierId);
  const save = useSetDeboursMandate();
  const [editing, setEditing] = useState(false);
  const [on, setOn] = useState('');
  const [ref, setRef] = useState('');
  const current = { on: dossier?.debours_mandate_on ?? null, ref: dossier?.debours_mandate_ref ?? null };

  function start() {
    const prefill = mandatePrefill(signedAt);
    setOn(current.on ?? prefill.on ?? '');
    setRef(current.ref ?? prefill.ref ?? '');
    setEditing(true);
  }

  async function submit() {
    try {
      await save.mutateAsync({ dossierId, on: on || null, ref: ref.trim() || null });
      toast.success('Mandat de débours enregistré');
      setEditing(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Enregistrement impossible');
    }
  }

  if (editing) {
    return (
      <div className="flex flex-wrap items-end gap-2 border-b border-[#e6e2d8] bg-[#fbfaf6] px-4 py-3 text-[13px]">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Date du mandat</span>
          <input type="date" value={on} onChange={(e) => setOn(e.target.value)} className="h-8 rounded-lg border border-neutral-300 bg-white px-2 text-[13px]" />
        </label>
        <label className="flex min-w-[220px] flex-1 flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Référence</span>
          <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Convention d’honoraires du …" className="h-8 rounded-lg border border-neutral-300 bg-white px-2 text-[13px]" />
        </label>
        <button type="button" onClick={() => setEditing(false)} className="h-8 rounded-full px-3 text-[13px] text-cime-text-muted hover:text-cime-text-primary">Annuler</button>
        <button type="button" onClick={submit} disabled={save.isPending} className="h-8 rounded-full bg-cime-primary px-3.5 text-[13px] font-semibold text-white disabled:opacity-50">Enregistrer</button>
      </div>
    );
  }

  const complete = !!current.on && !!current.ref;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-[#e6e2d8] px-4 py-2.5 text-[13px]">
      <span className="font-semibold text-cime-text-primary">Mandat de débours</span>
      {complete ? (
        <span className="text-cime-text-muted">{current.ref} · {formatAppDate(current.on!)}</span>
      ) : (
        <span className="text-cime-urgent">à renseigner — exigé par Polaris pour finaliser une facture de débours</span>
      )}
      {canEdit ? (
        <button type="button" onClick={start} className="ml-auto text-xs text-cime-primary underline underline-offset-2">
          {complete ? 'Modifier' : 'Renseigner'}
        </button>
      ) : null}
    </div>
  );
}

export function DossierExpensesSection({
  dossierId,
  signedAt,
  readOnly = false,
}: {
  dossierId: string;
  /** Date de signature de la convention d'honoraires (préremplit le mandat). */
  signedAt: string | null;
  readOnly?: boolean;
}) {
  const { data: expenses } = useExpenses(dossierId);
  const remove = useDeleteExpense();
  const canCreate = useCanAccessFeature('expenses_create') && !readOnly;
  const canMandate = useCanAccessFeature('debours_mandate_edit') && !readOnly;
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<DossierExpense | null>(null);

  const list = expenses ?? [];
  const toBill = list.filter((e) => !e.billed_at).reduce((sum, e) => sum + Math.round(Number(e.amount_ttc) * 100), 0) / 100;

  async function onDelete(expense: DossierExpense) {
    if (!window.confirm(`Supprimer « ${expense.label} » ?`)) return;
    try {
      await remove.mutateAsync(expense);
      toast.success('Ligne supprimée');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Suppression impossible');
    }
  }

  return (
    <section className="rounded-xl border border-neutral-200 bg-white">
      <header className="flex min-h-[52px] items-center justify-between gap-2 border-b border-[#e6e2d8] px-4 py-2.5">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">
          Frais et débours
          <span className="ml-2 font-normal tabular-nums text-neutral-400">{list.length}</span>
          {toBill > 0 ? (
            <span className="ml-3 font-semibold normal-case tracking-normal text-cime-gold-text">{formatEuros(toBill)} à refacturer</span>
          ) : null}
        </h3>
        {canCreate ? (
          <button
            type="button"
            onClick={() => { setEditing(null); setFormOpen(true); }}
            className="inline-flex h-[30px] items-center rounded-full bg-cime-primary px-[11px] text-xs font-semibold text-white hover:bg-[#02241c]"
          >
            Saisir
          </button>
        ) : null}
      </header>
      <MandateLine dossierId={dossierId} signedAt={signedAt} canEdit={canMandate} />
      {list.length === 0 ? (
        <p className="px-4 py-6 text-center text-xs text-neutral-400">Aucun frais ni débours sur ce dossier.</p>
      ) : (
        <ul>
          {list.map((e) => {
            const locked = !!e.billed_at;
            return (
              <li key={e.id} className="group flex items-center gap-3 border-b border-neutral-100 px-4 py-2.5 last:border-b-0">
                <span className={cn('inline-flex h-[22px] shrink-0 items-center rounded-full px-2.5 text-[11px] font-semibold', NATURE_PILL[e.nature as ExpenseNature])}>
                  {EXPENSE_NATURE_LABELS[e.nature as ExpenseNature]}
                </span>
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block truncate text-[13px] font-medium text-cime-text-primary">{e.label}</span>
                  <span className="block truncate text-xs text-neutral-400">
                    {[e.supplier, e.piece_number, formatAppDate(e.piece_date)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <ExpenseReceiptLink expense={e} />
                <span className="w-[86px] shrink-0 text-right text-[13px] font-semibold tabular-nums text-cime-text-primary">{formatEuros(e.amount_ttc)}</span>
                <span className={cn('w-[86px] shrink-0 text-right text-xs', locked ? 'text-[#2f7a55]' : 'text-cime-gold-text')}>
                  {locked ? 'Refacturé' : 'À refacturer'}
                </span>
                {canCreate && !locked ? (
                  <span className="flex shrink-0 items-center gap-2 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
                    <button type="button" aria-label={`Modifier ${e.label}`} onClick={() => { setEditing(e); setFormOpen(true); }} className="text-neutral-400 hover:text-cime-primary">
                      <FaPen className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" aria-label={`Supprimer ${e.label}`} onClick={() => onDelete(e)} className="text-neutral-400 hover:text-red-600">
                      <FaTrashCan className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ) : (
                  <span className="w-[42px] shrink-0" />
                )}
              </li>
            );
          })}
        </ul>
      )}
      <ExpenseFormDialog open={formOpen} onClose={() => setFormOpen(false)} dossierId={dossierId} expense={editing} />
    </section>
  );
}
